import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { TID } from "../constants/testIds";
import ChangePasswordDialog from "./ChangePasswordDialog";
import {
  LayoutDashboard, Building2, Users, HardHat, Wrench, CalendarClock,
  Link2, ScrollText, LogOut, Menu, X, Search, UserCog, KeyRound, ChevronDown, Briefcase
} from "lucide-react";

const baseNav = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", tid: TID.sidebarDashboard },
  { to: "/aziende", icon: Building2, label: "Aziende", tid: TID.sidebarCompanies },
  { to: "/lavoratori", icon: Users, label: "Lavoratori", tid: TID.sidebarWorkers },
  { to: "/cantieri", icon: HardHat, label: "Cantieri", tid: TID.sidebarSites },
  { to: "/attrezzature", icon: Wrench, label: "Attrezzature", tid: TID.sidebarEquipment },
  { to: "/scadenzario", icon: CalendarClock, label: "Scadenzario", tid: TID.sidebarScadenzario },
  { to: "/condivisioni", icon: Link2, label: "Condivisioni", tid: TID.sidebarShares },
  { to: "/ricerca", icon: Search, label: "Ricerca full-text", tid: "sidebar-search" },
];
const studioNav = baseNav.filter(n => n.to !== "/condivisioni");
const adminNav = [
  { to: "/studi", icon: Briefcase, label: "Studi di Consulenza", tid: "sidebar-studios" },
  { to: "/utenti", icon: UserCog, label: "Utenti", tid: "sidebar-users" },
  { to: "/audit", icon: ScrollText, label: "Registro Attività", tid: TID.sidebarAudit },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav_ = useNavigate();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [changePw, setChangePw] = useState(false);

  const handleLogout = async () => {
    await logout();
    nav_("/login");
  };

  const nav = user?.role === "admin" ? [...baseNav, ...adminNav] : user?.role === "studio" ? studioNav : baseNav.filter(n => user?.role === "operatore" || n.to !== "/condivisioni");

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-slate-900 fixed inset-y-0 left-0 z-30">
        <div className="px-5 py-6 border-b border-slate-800 flex items-center gap-3">
          <img src="/logo-studio-aragona.png" alt="Studio Aragona" className="w-12 h-12 rounded bg-white p-1 object-contain" />
          <div>
            <div className="font-display font-bold text-white text-base leading-tight">SicurDoc</div>
            <div className="text-xs text-slate-400 leading-tight">Studio Aragona</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.tid}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
              <n.icon size={18} />
              {n.label}
            </NavLink>
          ))}
          <div className="mt-6 px-3 pt-4 border-t border-slate-800 text-[11px] leading-relaxed text-slate-400" data-testid="sidebar-credits">
            <div className="text-slate-500 uppercase tracking-widest text-[10px] mb-1">Creato da</div>
            <div className="text-slate-200 font-semibold">Studio Aragona</div>
            <div className="text-slate-300">Dott. Aragona Adriano</div>
            <div>Consulente e Formatore D.Lgs. 81/08 — Sicurezza nei Luoghi di Lavoro</div>
            <div className="mt-1">R.S.P.P. Responsabile del Servizio di Prevenzione e Protezione D.Lgs. 81/08</div>
            <div className="mt-1">CSE — Coordinatore per la Sicurezza nell'Esecuzione dei Lavori</div>
          </div>
        </nav>
        <div className="p-4 border-t border-slate-800 relative">
          <button onClick={() => setMenu(m => !m)} data-testid="profile-menu" className="w-full text-left rounded-md hover:bg-slate-800 px-2 py-2 transition">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm text-white font-medium truncate">{user?.name || user?.email}</div>
                <div className="text-xs text-slate-500 capitalize">{user?.role === "studio" ? "Studio di Consulenza" : user?.role}</div>
              </div>
              <ChevronDown size={16} className={`text-slate-400 transition-transform ${menu ? "rotate-180" : ""}`} />
            </div>
          </button>
          {menu && (
            <div className="absolute bottom-full left-4 right-4 mb-1 bg-slate-800 border border-slate-700 rounded-md shadow-xl overflow-hidden" data-testid="profile-dropdown">
              <button onClick={() => { setMenu(false); setChangePw(true); }} data-testid="change-password-btn"
                className="w-full flex items-center gap-2 text-slate-200 hover:bg-slate-700 px-3 py-2 text-sm">
                <KeyRound size={15} /> Cambia password
              </button>
              <button onClick={handleLogout} data-testid={TID.logout}
                className="w-full flex items-center gap-2 text-slate-200 hover:bg-slate-700 px-3 py-2 text-sm border-t border-slate-700">
                <LogOut size={15} /> Esci
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-slate-900 text-white flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <img src="/logo-studio-aragona.png" alt="Studio Aragona" className="w-8 h-8 rounded bg-white p-0.5 object-contain" />
          <span className="font-display font-semibold">SicurDoc</span>
        </div>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-open"><Menu size={22} /></button>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-slate-900/70" onClick={() => setOpen(false)}>
          <aside className="w-72 max-w-[80%] h-full bg-slate-900 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 flex justify-between items-center border-b border-slate-800">
              <div className="flex items-center gap-2">
                <img src="/logo-studio-aragona.png" alt="Studio Aragona" className="w-9 h-9 rounded bg-white p-0.5 object-contain" />
                <span className="font-display font-bold text-white">SicurDoc</span>
              </div>
              <button onClick={() => setOpen(false)}><X size={22} className="text-white" /></button>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {nav.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                  className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
                  <n.icon size={18} /> {n.label}
                </NavLink>
              ))}
              <div className="mt-6 px-3 pt-4 border-t border-slate-800 text-[11px] leading-relaxed text-slate-400">
                <div className="text-slate-500 uppercase tracking-widest text-[10px] mb-1">Creato da</div>
                <div className="text-slate-200 font-semibold">Studio Aragona · Dott. Aragona Adriano</div>
                <div>Consulente e Formatore D.Lgs. 81/08 — Sicurezza nei Luoghi di Lavoro</div>
                <div className="mt-1">R.S.P.P. Responsabile del Servizio di Prevenzione e Protezione D.Lgs. 81/08</div>
                <div className="mt-1">CSE — Coordinatore per la Sicurezza nell'Esecuzione dei Lavori</div>
              </div>
            </nav>
            <div className="p-4 border-t border-slate-800 space-y-1">
              <button onClick={() => { setOpen(false); setChangePw(true); }} data-testid="mobile-change-password"
                className="w-full flex items-center gap-2 text-slate-300 hover:text-white bg-slate-800 px-3 py-2 rounded-md text-sm">
                <KeyRound size={16} /> Cambia password
              </button>
              <button onClick={handleLogout}
                className="w-full flex items-center gap-2 text-slate-300 hover:text-white bg-slate-800 px-3 py-2 rounded-md text-sm">
                <LogOut size={16} /> Esci
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>
      {changePw && <ChangePasswordDialog onClose={() => setChangePw(false)} />}
    </div>
  );
}
